import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import DeleteConfirmModal from 'components/DeleteConfirmModal';
import FilmIncentiveEditorModal from 'components/FilmIncentiveEditorModal';
import { useFilmIncentives } from 'hooks/useFilmIncentives';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useTagQuery } from 'utils/api/queries';
import { formatCurrency, formatPercentage } from 'utils/helper';
import { AddIcon } from 'utils/icons';

const FilmIncentivesSection = ({ budgetId }) => {
  const { budgetData, hasEditAccess, refetchBudgetId } = useHomeScreenContext();
  const currency = budgetData?.primary_currency || 'USD';
  const { incentives, isLoading, saveIncentive, deleteIncentive } =
    useFilmIncentives(budgetId, Boolean(budgetId));
  const { data: tagsResponse } = useTagQuery.useTagsQuery({
    filters: { budget: budgetId },
    enabled: Boolean(budgetId),
  });
  const tags = useMemo(() => tagsResponse?.data || [], [tagsResponse]);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const editingIncentive =
    incentives.find(incentive => incentive.id === editingId) || null;

  const closeEditor = () => {
    if (isLoading) return;
    setEditingId(null);
    setIsAdding(false);
  };

  const save = async payload => {
    await saveIncentive(payload);
    setEditingId(null);
    setIsAdding(false);
    refetchBudgetId?.();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteIncentive(deleteTarget.id);
    setDeleteTarget(null);
    setEditingId(null);
    refetchBudgetId?.();
  };

  const statusText = incentive => {
    if (incentive.missing_exchange_rate) {
      return 'Incomplete — a selected row has no exchange rate';
    }
    if (!incentive.configuration_complete) {
      return 'Incomplete — select a tag or enter qualified spend';
    }
    if (!incentive.threshold_met) {
      return `Below the ${formatCurrency(
        incentive.minimum_qualified_spend,
        currency
      )} minimum`;
    }
    return `${formatCurrency(
      incentive.qualified_spend || 0,
      currency
    )} qualified spend`;
  };

  return (
    <section className="film-incentives-section">
      {hasEditAccess() && !isAdding && editingId === null && (
        <div className="swimlane-item swimlane-item--action">
          <button
            type="button"
            className="action-add-item film-incentives-section__add"
            onClick={() => setIsAdding(true)}
          >
            Add Credit <AddIcon className="action-add-item__icon" />
          </button>
        </div>
      )}

      {incentives.map((incentive, index) => (
        <button
          type="button"
          key={incentive.id}
          className={`film-incentive-row film-incentive-row--${
            index % 2 === 0 ? 'even' : 'odd'
          }`}
          onClick={() => hasEditAccess() && setEditingId(incentive.id)}
          title={statusText(incentive)}
        >
          <span className="film-incentive-row__info">
            <span className="film-incentive-row__name-wrapper">
              <span className="film-incentive-row__name">
                {incentive.name} ({formatPercentage(incentive.rate_percent)}%)
              </span>
              <span className="visually-hidden">{statusText(incentive)}</span>
            </span>
            <span
              className={`film-incentive-row__amount${
                incentive.estimate_complete ? '' : ' is-incomplete'
              }`}
            >
              {incentive.estimate_complete
                ? `−${formatCurrency(incentive.net_credit || 0, currency)}`
                : 'Incomplete'}
            </span>
          </span>
        </button>
      ))}

      <DeleteConfirmModal
        showModal={Boolean(deleteTarget)}
        handleOnClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        confirmText="Are you sure you want to delete this film tax credit estimate?"
        itemName={deleteTarget?.name || ''}
        confirmLabel="Delete"
        disabled={isLoading}
      />

      <FilmIncentiveEditorModal
        show={isAdding || Boolean(editingIncentive)}
        onHide={closeEditor}
        incentive={editingIncentive}
        budgetId={budgetId}
        tags={tags}
        onSave={save}
        onDelete={
          editingIncentive
            ? () => {
                setEditingId(null);
                setDeleteTarget(editingIncentive);
              }
            : null
        }
        isSaving={isLoading}
      />
    </section>
  );
};

FilmIncentivesSection.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default FilmIncentivesSection;
